import React from "react";
import {
  Drawer,
  Form,
  Input,
  Button,
  Upload,
  Select,
  AutoComplete,
  Typography,
  Space,
  Tag,
  Popconfirm,
} from "antd";
import { ORGANS, subRegionsFor } from "../utils/regionTaxonomy";
import { groupPicks, describeGroup } from "../utils/uploadGrouping";
import {
  InboxOutlined,
  BookOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  FileImageOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  FileZipOutlined,
} from "@ant-design/icons";
import { GiMicroscope, GiMagnifyingGlass } from "react-icons/gi";
import { LiaXRaySolid } from "react-icons/lia";

const { Text } = Typography;

const SectionTitle = ({ icon, children, style }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 16,
      color: "var(--color-text-secondary)",
      ...style,
    }}
  >
    {icon}
    <Text
      strong
      style={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "var(--color-text-secondary)",
      }}
    >
      {children}
    </Text>
  </div>
);

// Commit a picked batch in one go. rc-upload calls beforeUpload once per file
// with the whole batch as the second argument, so appending on every call would
// mean hundreds of form updates for a single exam folder.
const appendBatch = (form, name) => (file, batch) => {
  const isLastOfBatch = file === batch[batch.length - 1];
  if (isLastOfBatch) {
    const current = form.getFieldValue(name) || [];
    form.setFieldsValue({ [name]: [...current, ...batch] });
  }
  return false; // never auto-upload; enqueueCaseFiles handles it on submit
};

// One row per exam folder / archive instead of one row per slice.
const PickedSummary = ({ groups, onRemoveGroup }) => {
  if (groups.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      {groups.map((group) => (
        <div
          key={group.key}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 8px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            marginBottom: 6,
          }}
        >
          {group.isFolder ? <FolderOpenOutlined /> : <FileZipOutlined />}
          <span
            style={{
              flex: 1,
              fontSize: 12,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {group.label}
          </span>
          <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
            {describeGroup(group)}
          </Text>
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onRemoveGroup(group)}
          />
        </div>
      ))}
    </div>
  );
};

const DropzoneBody = ({ icon, title, sub }) => (
  <div style={{ padding: "2px 6px" }}>
    <p className="ant-upload-drag-icon" style={{ marginBottom: 2 }}>
      {icon}
    </p>
    <p className="ant-upload-text" style={{ fontSize: 12, margin: 0, fontWeight: 500 }}>
      {title}
    </p>
    <p className="ant-upload-hint" style={{ fontSize: 11, margin: 0 }}>
      {sub}
    </p>
  </div>
);

// Radiology arrives in two shapes: an archive / loose instances, or a whole exam
// folder. `directory` can't go on a single Dragger — it would stop users picking
// a .zip — so each case gets its own dropzone, both writing to the same field.
// Controlled by Form.Item through valuePropName="fileList".
//
// No `accept` on the file zone, deliberately: PACS instances are usually
// extensionless and an accept filter would hide them in the OS dialog.
// enqueueCaseFiles validates the picks instead.
const RadiologyDropzones = ({ fileList = [], onChange }) => {
  const commitBatch = (file, batch) => {
    // rc-upload calls beforeUpload once per file with the whole batch; commit
    // once, otherwise a 400-slice folder means 400 form updates.
    if (file === batch[batch.length - 1]) onChange([...fileList, ...batch]);
    return false; // never auto-upload; enqueueCaseFiles handles submit
  };

  const removeGroup = (group) => {
    const dropped = new Set(group.entries);
    onChange(fileList.filter((entry) => !dropped.has(entry)));
  };

  return (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Upload.Dragger
            multiple
            showUploadList={false}
            beforeUpload={commitBatch}
            style={{ height: "100%" }}
          >
            <DropzoneBody
              icon={<InboxOutlined style={{ fontSize: 20 }} />}
              title="Fichiers"
              sub=".zip ou fichiers DICOM"
            />
          </Upload.Dragger>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Upload.Dragger
            directory
            multiple
            showUploadList={false}
            beforeUpload={commitBatch}
            style={{ height: "100%" }}
          >
            <DropzoneBody
              icon={<FolderOpenOutlined style={{ fontSize: 20 }} />}
              title="Dossier d'examen"
              sub="toute l'arborescence"
            />
          </Upload.Dragger>
        </div>
      </div>
      <PickedSummary groups={groupPicks(fileList)} onRemoveGroup={removeGroup} />
    </>
  );
};

const ImageUploadField = ({ label, hint, name, accept, form, icon, bulk }) => (
  <Form.Item
    label={
      <Space size={6} align="center">
        <span style={{ display: "inline-flex", alignItems: "center" }}>{icon}</span>
        <span>{label}</span>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {hint}
        </Text>
      </Space>
    }
    name={name}
    valuePropName="fileList"
    getValueFromEvent={
      bulk ? (value) => value : (e) => (Array.isArray(e) ? e : e && e.fileList)
    }
    style={{ marginBottom: 12 }}
  >
    {bulk ? (
      <RadiologyDropzones />
    ) : (
      <Upload.Dragger
        accept={accept}
        multiple
        onRemove={(file) => {
          const fileList = form.getFieldValue(name) || [];
          form.setFieldsValue({
            [name]: fileList.filter((item) => item.uid !== file.uid),
          });
        }}
        beforeUpload={appendBatch(form, name)}
      >
        <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
          <InboxOutlined style={{ fontSize: 22 }} />
        </p>
        <p className="ant-upload-text" style={{ fontSize: 12, margin: 0 }}>
          Glisser-déposer ou cliquer
        </p>
      </Upload.Dragger>
    )}
  </Form.Item>
);

const MODALITY_LABEL = { macro: "Macro", radio: "Radio", histo: "Histo" };

const ExistingImages = ({ images, onRemoveImage }) => (
  <div style={{ marginBottom: 16 }}>
    {images.map((img) => (
      <div
        key={img.id}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "6px 8px",
          border: "1px solid var(--color-border)",
          borderRadius: 6,
          marginBottom: 6,
        }}
      >
        {img.thumbnailUrl ? (
          <img
            src={img.thumbnailUrl}
            alt={img.name}
            style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          <div style={{ width: 40, height: 40, borderRadius: 4, background: "var(--color-border)" }} />
        )}
        <Tag style={{ marginRight: 0 }}>{MODALITY_LABEL[img.modality] || img.modality}</Tag>
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {img.name || img.modality}
        </span>
        <Popconfirm
          title="Retirer cette image du cas ?"
          okText="Retirer"
          cancelText="Annuler"
          okButtonProps={{ danger: true }}
          onConfirm={() => onRemoveImage(img.id)}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </div>
    ))}
  </div>
);

const CaseFormModal = ({
  open,
  onCancel,
  onFinish,
  form,
  isEditing,
  courseOptions,
  existingImages = [],
  onRemoveImage,
}) => {
  const showCourseSelect =
    Array.isArray(courseOptions) && courseOptions.length > 0;

  // Region suggestions cascade from the chosen organ; both accept free text.
  const organ = Form.useWatch("organ", form);
  const acFilter = (input, option) =>
    (option?.value || "").toLowerCase().includes((input || "").toLowerCase());

  return (
    <Drawer
      open={open}
      onClose={onCancel}
      title={isEditing ? "Modifier le cas" : "Ajouter un cas"}
      width={560}
      destroyOnClose
    >
      <Form form={form} onFinish={onFinish} layout="vertical">
        <SectionTitle icon={<FileTextOutlined />}>Informations</SectionTitle>

        {showCourseSelect && (
          <Form.Item
            label="Cours"
            name="course"
            rules={[{ required: true, message: "Cours requis." }]}
          >
            <Select
              showSearch
              placeholder="Sélectionner un cours"
              optionFilterProp="label"
              options={courseOptions}
              suffixIcon={<BookOutlined />}
            />
          </Form.Item>
        )}

        <Form.Item
          label="Pathologie"
          name="pathology"
          rules={[
            { required: true, message: "Nom de la pathologie requis." },
          ]}
        >
          <Input placeholder="Ex. Tumeur hépatique" />
        </Form.Item>

        <Form.Item
          label="Organe / système"
          name="organ"
          tooltip="Niveau large (ex. Abdomen). Vous pouvez saisir une valeur libre."
        >
          <AutoComplete
            options={ORGANS.map((o) => ({ value: o }))}
            filterOption={acFilter}
            allowClear
            placeholder="Ex. Abdomen"
          />
        </Form.Item>

        <Form.Item
          label="Région anatomique"
          name="region"
          rules={[{ required: true, message: "Région anatomique requise." }]}
          tooltip="Niveau fin (ex. Foie). Suggestions selon l'organe; saisie libre possible."
        >
          <AutoComplete
            options={subRegionsFor(organ).map((r) => ({ value: r }))}
            filterOption={acFilter}
            allowClear
          >
            <Input
              placeholder="Ex. Foie"
              prefix={
                <EnvironmentOutlined
                  style={{ color: "var(--color-text-secondary)" }}
                />
              }
            />
          </AutoComplete>
        </Form.Item>

        <Form.Item
          label="Résumé clinique"
          name="clinicalSummary"
          rules={[{ required: true, message: "Résumé clinique requis." }]}
        >
          <Input.TextArea
            rows={5}
            placeholder="Décrivez brièvement le contexte clinique du cas…"
          />
        </Form.Item>

        <SectionTitle
          icon={<FileImageOutlined />}
          style={{ marginTop: 24 }}
        >
          Imagerie
        </SectionTitle>

        {isEditing && existingImages.length > 0 && onRemoveImage && (
          <>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
              Images existantes
            </Text>
            <ExistingImages images={existingImages} onRemoveImage={onRemoveImage} />
          </>
        )}

        <ImageUploadField
          icon={<GiMagnifyingGlass />}
          label="Macroscopique"
          hint="(.png, .jpg, .jpeg)"
          name="macroImages"
          accept=".png,.jpg,.jpeg"
          form={form}
        />
        <ImageUploadField
          icon={<LiaXRaySolid />}
          label="Radiologique"
          hint="(dossier, .zip, ou DICOM)"
          name="radioImages"
          form={form}
          bulk
        />
        <ImageUploadField
          icon={<GiMicroscope />}
          label="Histologique"
          hint="(.svs, .tiff, .ndpi, .mrxs, ... ou .png/.jpg)"
          name="histoImages"
          accept=".svs,.tiff,.tif,.ndpi,.mrxs,.scn,.vms,.vmu,.png,.jpg,.jpeg"
          form={form}
        />

        <Space style={{ marginTop: 16 }}>
          <Button type="primary" htmlType="submit">
            {isEditing ? "Enregistrer" : "Ajouter"}
          </Button>
          <Button onClick={onCancel}>Annuler</Button>
        </Space>
      </Form>
    </Drawer>
  );
};

export default CaseFormModal;
